import * as React from 'react';
import { CSSProperties } from 'react';
import { makeTestId } from '../utils/Declarative';
import * as Geometry from '../utils/Geometry';
import { Position } from '../utils/Geometry';
import { StackingComponentHOC, StackingComponentProps, StackingRootContext } from '../utils/StackingComponent';
import * as ViewportHelpers from '../utils/ViewportHelpers';

const Styles: {
  Floater: CSSProperties
} = {
  Floater: {
    position: 'absolute',
    outline: 0,
    pointerEvents: 'auto'
  }
};

export interface FloaterProps extends StackingComponentProps {
  testId: string;
  customFloaterStyle?: CSSProperties;
  children?: React.ReactNode;
  handleFloaterRef?: (ref: HTMLDivElement | null) => void;
  /**
   * At the moment vizclient uses z-indexing in several places to order stacking.  Until this is resolved we need to pass in
   * a z-index prop to allow vizclient to properly stack things.
   * TODO: Feature 579115:Web: Unify Stacking strategy in viz client
   */
  zIndex?: number;
  shouldRecenterOnUpdate?: boolean;
}

export interface FloaterState { floaterElem: HTMLElement | null; }

export class FloaterWidgetDefault extends React.Component<FloaterProps, FloaterState> {
  private floater: HTMLElement | null = null;
  private centerTimeoutHandle: number | null = null;

  public state: FloaterState = { floaterElem: null };

  private floaterRefHandler = (ref: HTMLDivElement | null): void => {
    this.floater = ref;
    this.setState({ floaterElem: ref });

    if (!!this.props.handleFloaterRef) {
      this.props.handleFloaterRef(ref);
    }
  }

  /** Gets a rect of the floater's size that is centered in the visual viewport. */
  private getCenteredFloaterRectInLayoutCoords(): ClientRect {
    const viewportRectInLayoutCoords = ViewportHelpers.getVisualViewportRectRelativeToLayoutViewport();
    const floaterSize = { width: this.floater!.offsetWidth, height: this.floater!.offsetHeight };
    const centeredFloaterPosInLayoutCoords = Geometry.computePositionToCenterAreaInBounds(floaterSize, viewportRectInLayoutCoords);
    return Geometry.makeClientRect(centeredFloaterPosInLayoutCoords, floaterSize);
  }

  /** Gets a rect that represents a lower bound on the extents of the page. */
  private getMinimumPageRectInLayoutCoords(): ClientRect {
    /**
     * It's tricky to get the full size of the page because super-wide elements can stretch the page beyond the document's
     * initial containing block, but getting a minimum bound on the page size is good enough for our purposes.
     */
    const pagePosInLayoutCoords = ViewportHelpers.getElementPositionRelativeToLayoutViewport(document.documentElement);
    const minimumPageSizeInferredFromLayoutViewport = ViewportHelpers.getLayoutViewportSize();

    const viewportRectInLayoutCoords = ViewportHelpers.getVisualViewportRectRelativeToLayoutViewport();
    const minimumPageSizeInferredFromVisualViewport = {
      width: viewportRectInLayoutCoords.right - pagePosInLayoutCoords.left,
      height: viewportRectInLayoutCoords.bottom - pagePosInLayoutCoords.top
    };

    const minimumPageSize =
      Geometry.getMaxWidthAndHeight(minimumPageSizeInferredFromLayoutViewport, minimumPageSizeInferredFromVisualViewport);
    return Geometry.makeClientRect(pagePosInLayoutCoords, minimumPageSize);
  }

  /**
   * Centers the floater in the visual viweport.
   */
  public centerInVisualViewport(): void {
    const centeredFloaterRectInLayoutCoords = this.getCenteredFloaterRectInLayoutCoords();
    const minimumPageRectInLayoutCoords = this.getMinimumPageRectInLayoutCoords();
    /** Constrain position to prevent us from positioning outside of the page, which could cause unwanted overflow. */
    const constrainedFloaterPosInLayoutCoords =
      Geometry.computePositionToContainBounds(centeredFloaterRectInLayoutCoords, minimumPageRectInLayoutCoords);

    this.setPosition(constrainedFloaterPosInLayoutCoords);
  }

  public getFloaterClientRect(): ClientRect {
    return this.floater!.getBoundingClientRect();
  }

  /**
   * Set the position of the floater widget. The position should be in layout viewport coordinates.
   */
  public setPosition(pos: Position): void {
    const offsetParent = (this.floater!.offsetParent as HTMLElement) || document.documentElement;
    const offsetParentPosInLayoutCoords = ViewportHelpers.getElementPositionRelativeToLayoutViewport(offsetParent);

    const posInOffsetParentCoords = Geometry.subtractPositions(pos, offsetParentPosInLayoutCoords);
    this.floater!.style.top = posInOffsetParentCoords.top + 'px';
    this.floater!.style.left = posInOffsetParentCoords.left + 'px';
  }

  public componentDidMount(): void {
    this.centerInVisualViewport();
  }

  public componentDidUpdate(prevProps: FloaterProps, prevState: FloaterState): void {
    // only recenter for prop updates, not state updates
    if (this.props.shouldRecenterOnUpdate && prevState.floaterElem === this.state.floaterElem) {
      this.clearDeferredCentering();
      /* Positioning is size-sensitive, so make sure the DOM has been drawn first before positioning */
      this.centerTimeoutHandle = window.setTimeout(() => {
        this.centerInVisualViewport();
      }, 0);
    }
  }

  private clearDeferredCentering(): void {
    if (this.centerTimeoutHandle) {
      clearTimeout(this.centerTimeoutHandle);
      this.centerTimeoutHandle = null;
    }
  }

  public componentWillUnmount(): void {
    this.clearDeferredCentering();
  }

  public render(): JSX.Element {
    return (
      <StackingRootContext.Provider value={this.state.floaterElem}>
        <div
          children={this.props.children}
          data-tb-test-id={makeTestId(this.props.testId, 'Floater')}
          ref={this.floaterRefHandler}
          style={{ ...Styles.Floater, ...this.props.customFloaterStyle }}
        />
      </StackingRootContext.Provider>
    );
  }
}

export const FloaterWidget = StackingComponentHOC(FloaterWidgetDefault);
FloaterWidget.displayName = 'FloaterWidget';
